import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Box, CircularProgress, Container } from '@mui/material';
import parse from 'html-react-parser';

import HtmlContent from '@components/HtmlContent/HtmlContent';
import UserProfileModal from '@components/UserProfileModal/UserProfileModal';
import { userDataSelector } from '@store/features/auth/user/selectors';
import {
  canAccessAdminSelector,
  canAccessMedicationLookupSelector,
  canAccessSupplementInquirySelector,
  dashboardOrgDataSelector,
} from '@store/features/dashboardOrgInfo/selectors';

import DietarySupplementInquiryCard from './DietarySupplementInquiryCard/DietarySupplementInquiryCard';
import InfoCard from './InfoCard/InfoCard';
import InquiriesAdminCard from './InquiriesAdminCard/InquiriesAdminCard';
import InquiriesCard from './InquiriesCard/InquiriesCard';
import MedicationStatusLookupCard from './MedicationStatusLookupCard/MedicationStatusLookupCard';

import './style.scss';

const HomePage: FC = () => {
  const user = useSelector(userDataSelector);
  const dashboardOrgInfo = useSelector(dashboardOrgDataSelector);
  const canAccessMedicationLookup = useSelector(canAccessMedicationLookupSelector);
  const canAccessSupplementInquiry = useSelector(canAccessSupplementInquirySelector);
  const canAccessAdmin = useSelector(canAccessAdminSelector);

  const { t } = useTranslation();

  const [isUserProfileModalOpen, setIUserProfileModalOpen] = useState<boolean>(false);

  useEffect(() => {
    user && !user.organizationId && !user.isAdmin && setIUserProfileModalOpen(true);
  }, [user]);

  if (!dashboardOrgInfo) {
    return (
      <>
        <UserProfileModal
          open={isUserProfileModalOpen}
          onClose={(): void => setIUserProfileModalOpen(false)}
        />
        <Container className="page page_center">
          <CircularProgress />
        </Container>
      </>
    );
  }

  const { headlineText, globalMessage, infoTileText } = dashboardOrgInfo;

  return (
    <>
      <UserProfileModal
        open={isUserProfileModalOpen}
        onClose={(): void => setIUserProfileModalOpen(false)}
      />

      <Container maxWidth="lg" className="page homepage">
        {headlineText ? (
          <Box className="homepage__welcome-block">
            <HtmlContent>{headlineText}</HtmlContent>
          </Box>
        ) : (
          <Box className="homepage__welcome-block">
            <div>
              <h1>{t('common.welcome')}</h1>
            </div>
          </Box>
        )}

        {globalMessage && globalMessage !== 'null' && (
          <Box className="homepage__global-message">
            <HtmlContent>{parse(globalMessage) as string}</HtmlContent>
          </Box>
        )}

        <Box className="homepage__cards">
          {canAccessSupplementInquiry && <DietarySupplementInquiryCard />}

          {canAccessSupplementInquiry && <InquiriesCard />}

          {canAccessMedicationLookup && <MedicationStatusLookupCard />}

          {canAccessAdmin && <InquiriesAdminCard />}

          {infoTileText && <InfoCard content={infoTileText} />}
        </Box>
      </Container>
    </>
  );
};

export default HomePage;
